import LoadingSpinner from '@atoms/LoadingSpinner/LoadingSpinner';
import { IEditWishlist } from '@models/IEditWishlist';
import { Wishlist, WishlistEntry } from '@models/IWishlistCardSwitch';
import SizeEnum from '@models/enums/SizeEnum';
import ThemeEnum from '@models/enums/ThemeEnum';
import WishlistEmptyState from '@molecules/WishlistEmptyState/WishlistEmptyState';
import WishlistCarousel from '@organisms/WishlistCarousel/WishlistCarousel';
import { useLikedItemsStore, useWishlistStore } from '@state/WishlistStore';
import { useEffect, useMemo } from 'react';

interface Props extends IEditWishlist {
  readonly createListEndpoint: string;
  readonly emptyStateSubText: string;
  readonly emptyStateTitle: string;
  readonly refreshImageUrlEndpoint: string;
  readonly shareLabel: string;
  readonly sharePageUrl: string;
}

const WishlistListing = ({
  copyModal,
  createListEndpoint,
  deleteModal,
  editDropdown,
  editLabel,
  emptyStateSubText,
  emptyStateTitle,
  refreshImageUrlEndpoint,
  renameModal,
  shareLabel,
  sharePageUrl,
}: Props) => {
  const labels = {
    copyModal,
    deleteModal,
    editDropdown,
    editLabel,
    renameModal,
  };

  const { likedItems, fetchLikedItems } = useLikedItemsStore();
  const {
    wishlists,
    fetchWishlists,
    isLoading: isWishlistsLoading,
  } = useWishlistStore();

  useEffect(() => {
    (async () => {
      try {
        await fetchLikedItems();
        await fetchWishlists();
      } catch (error) {
        console.error('Failed to fetch data', error);
      }
    })();
  }, [fetchLikedItems, fetchWishlists]);

  const handleShare = async (id: number) => {
    const sharedWishlist = wishlists.find((wishlist) => wishlist.id === id);
    if (!sharedWishlist) return;

    const transformedLikes = sharedWishlist.likes
      .map((likeId) => {
        const likedItem = likedItems.find((item) => item.id === likeId);
        if (likedItem) {
          return {
            entity: {
              id: likedItem.entity.id,
              collection: likedItem.entity.collection,
              data: likedItem.entity.data ?? likedItem.entity.fields,
              newModel: likedItem.entity.newModel,
            },
            id: likedItem.id,
          };
        }
        return null;
      })
      .filter((item) => item !== null);

    const wishlistData = {
      name: sharedWishlist.name,
      likes: transformedLikes,
      pageUrl: window.location.pathname,
    };

    try {
      const response = await fetch(
        `${window.location.origin}${createListEndpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(wishlistData),
        },
      );

      if (response.ok) {
        const listId = await response.text();

        const sharePage = `${sharePageUrl}?listid=${listId}`;
        window.location.href = sharePage;
      } else {
        console.error('Failed to share wishlist. Response:', response);
      }
    } catch (error) {
      console.error('Error sharing wishlist:', error);
    }
  };

  const getWishlistCards = (likedItems: WishlistEntry[], lists: Wishlist[]) => {
    const likesMap = new Map(
      likedItems.map(
        ({
          id,
          entity: { data, collection, id: entityId, newModel, fields },
        }) => [id, { data, collection, newModel, entityId, fields }],
      ),
    );

    const result: WishlistEntry[] = [];
    const addedItems = new Set();

    lists.forEach(({ likes: listLikes }) => {
      if (Array.isArray(listLikes)) {
        listLikes.forEach((likeId) => {
          const _likeId = likeId as number;
          if (likesMap.has(_likeId) && !addedItems.has(likeId) && _likeId) {
            const { data, collection, newModel, fields, entityId } =
              likesMap.get(_likeId)!;

            result.push({
              entity: {
                id: entityId,
                collection,
                data,
                fields,
                newModel,
              },
              id: _likeId,
            });
            addedItems.add(likeId);
          }
        });
      }
    });

    return result;
  };

  const wishlistEntries = useMemo(
    () => getWishlistCards(likedItems, wishlists),
    [likedItems, wishlists, getWishlistCards],
  );

  if (isWishlistsLoading)
    return (
      <div className="tw-container tw-flex tw-h-[70vh] tw-items-center tw-justify-center tw-py-3xl">
        <LoadingSpinner
          size={SizeEnum.Large}
          loadingLabel="loading"
          theme={ThemeEnum.Dark}
        />
      </div>
    );
  return (
    <section className="tw-pt-3xl tw-text-black">
      {!wishlists.length ? (
        <WishlistEmptyState
          title={emptyStateTitle}
          subText={emptyStateSubText}
        />
      ) : (
        <div className="tw-component-spacing">
          {wishlists.map(({ name, likes, id }) => (
            <WishlistCarousel
              refreshImageUrlEndpoint={refreshImageUrlEndpoint}
              key={id}
              wishlistId={id}
              title={name}
              items={
                !!likes
                  ? wishlistEntries.filter((entry) =>
                      likes.includes(Number(entry.id)),
                    )
                  : []
              }
              shareLabel={shareLabel}
              labels={labels}
              emptyStateSubText={emptyStateSubText}
              emptyStateTitle={emptyStateTitle}
              onShare={() => handleShare(id)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default WishlistListing;
